// Which CATALOG this page is reading: the environment's own, or a per-PR
// PREVIEW of it (`?catalog=pr-<n>`).
//
// The selection lives in the URL, and the router RETAINS it across navigations
// (see router.tsx). That is what makes the link a pull request comments
// actually work: an analyst opens `/?catalog=pr-12`, clicks into a dashboard,
// opens a chart, and is still reading their own unmerged catalog the whole
// way — and the URL they copy out of the address bar at any point takes a
// colleague to the same place.
//
// `activePreviewCatalog()` reads it ONCE, at module load, and that is correct
// rather than convenient: retention means no in-app navigation can change the
// value, and the one thing that clears it (the banner's Leave preview) is a
// full page load on purpose. Capturing makes the value available to `Boot()`,
// which renders ABOVE the router and so has no search hook to read — and it
// keeps a query key from silently changing mid-session, which is the failure
// mode a live read would have.
//
// The key SHAPE is pinned here as well as on the backend
// (`preview_catalog.PREVIEW_KEY_RE`). Not belt-and-braces: this copy is what
// keeps a hand-edited `?catalog=` out of a query key and out of the banner,
// where an arbitrary string would be reflected onto the page. The backend's
// copy decides what is SERVED; this one decides what is DRAWN.

/** `pr-` followed by a plausible pull request number, and nothing else. */
const PREVIEW_KEY = /^pr-[0-9]{1,7}$/;

/** The preview key in a URL query string, or `null` for the environment's own
 *  catalog. `null` rather than `undefined` so a caller cannot accidentally
 *  spread it back into a search object and re-add the param it just cleared. */
export function previewCatalogFromSearch(search: string): string | null {
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get("catalog");
  } catch {
    return null;
  }
  return raw !== null && PREVIEW_KEY.test(raw) ? raw : null;
}

const ACTIVE = previewCatalogFromSearch(
  typeof window === "undefined" ? "" : window.location.search,
);

/** The preview this page load is reading, or `null`. See the header for why
 *  this is captured rather than read live. */
export function activePreviewCatalog(): string | null {
  return ACTIVE;
}

/** The router's `validateSearch` for the shared `catalog` param, declared on
 *  the ROOT route so every page under it carries the selection and no
 *  individual route has to know about previews.
 *
 *  A malformed value is DROPPED rather than rejected: a bad `?catalog=` should
 *  land somebody on the ordinary catalog, not on an error page for a param
 *  they probably did not type. */
export function validateCatalogSearch(search: Record<string, unknown>): { catalog?: string } {
  const raw = search.catalog;
  return typeof raw === "string" && PREVIEW_KEY.test(raw) ? { catalog: raw } : {};
}

/** The pull request number a key names, for display: `"pr-12"` becomes
 *  `"#12"`, which is what a reader recognises from GitHub. */
export function previewCatalogLabel(key: string): string {
  return `#${key.slice("pr-".length)}`;
}

/** Append the catalog selector to an API path, when one is active.
 *  One helper, so the three dashboard routes cannot disagree about the
 *  parameter's name and a route that forgets it is a missing call rather than
 *  a subtly different string. */
export function withCatalog(path: string, key: string | null): string {
  if (key === null) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}catalog=${encodeURIComponent(key)}`;
}

/** How long the ingest may go without LOOKING at a preview before the banner
 *  stops promising that a push will arrive.
 *
 *  Three ticks. The service reaches every preview every five minutes, so one
 *  tick of slack is normal and two is a slow tick; past three the honest
 *  reading is that this preview is not being reached, and there are real ways
 *  for that to be true while the page renders perfectly: the ingest cap dropped
 *  it, the tick's time budget keeps deferring it, or the tick itself is
 *  failing. */
const STALE_PREVIEW_MS = 15 * 60 * 1000;

/** Whether anything has reached this preview lately.
 *
 *  "Nothing has reached it", deliberately, rather than "the ingest has not
 *  reached it": this timestamp is served through a per-process cache whose
 *  freshness probe can itself fail, so a Postgres blip on the READ side makes
 *  an old one too, while the ingest is ticking along perfectly. Both are worth
 *  telling a reader about and neither is worth pointing them at a publish
 *  workflow that may have run fine, so the wording covers what is actually
 *  known: something between the push and this page is not moving.
 *
 *  READS `checkedAt`, NOT `syncedAt`, and the distinction is the whole point.
 *  `syncedAt` moves only when the tree CHANGED, so a preview whose pull request
 *  paused for an hour has an old one and is perfectly healthy: keying staleness
 *  off it warned about almost every preview, which is the same as warning about
 *  none. `checkedAt` moves on every tick that reached the preview, whatever it
 *  concluded, so an old one means what this function claims it means.
 *
 *  `false` for a missing or unparseable timestamp: the banner says nothing at
 *  all in that case, and inventing staleness from an absent fact would be its
 *  own kind of wrong. */
export function isPreviewStale(checkedAt: string | null | undefined, now = Date.now()): boolean {
  if (!checkedAt) return false;
  const at = Date.parse(checkedAt);
  return Number.isFinite(at) && now - at > STALE_PREVIEW_MS;
}

/** Whether the page at `pathname` is one the preview selection actually
 *  changes.
 *
 *  The selection is app-wide and the router retains it everywhere, which is
 *  right: a reader who wanders into the studio and comes back is still in their
 *  preview. But only the CATALOG routes read a preview catalog. `/studio`,
 *  `/taggers`, `/corpus`, `/experiments` and `/a/` are this environment's own
 *  data whatever `?catalog=` says, so a banner reading "Nothing here is merged"
 *  over them is the inverse of the one claim this banner exists to make. It is
 *  drawn where it is true and not where it is not.
 *
 *  `/` (the catalog) and `/d/<slug>` (one dashboard) are that set. A new route
 *  that renders catalog content belongs here too. */
export function previewAppliesToPath(pathname: string): boolean {
  return pathname === "/" || pathname === "/d" || pathname.startsWith("/d/");
}

/** Leave the preview by RELOADING onto the same path with no `?catalog=`.
 *
 *  A router navigation would not do: the key is captured once per page load and
 *  the query cache is scoped by it (see the header), so the page would go on
 *  reading the preview while the URL said otherwise.
 *
 *  Shared by the banner and by `Boot()`'s "no such preview" state, and the
 *  second one is not a nicety. `?catalog=` is retained onto every route, so a
 *  bookmark taken at `/studio?catalog=pr-12` outlives the pull request: after
 *  the merge prunes the preview, the boot probe 404s and the WHOLE app is
 *  unreachable, on a page previews never applied to, with no way out but
 *  editing the URL by hand. */
export function leavePreview(): void {
  const { pathname, hash } = window.location;
  const search = new URLSearchParams(window.location.search);
  search.delete("catalog");
  const query = search.toString();
  window.location.assign(`${pathname}${query ? `?${query}` : ""}${hash}`);
}
